import React, { useEffect, useState } from "react"
import { Flex } from "@chakra-ui/react"
import { Route, Switch, useLocation } from "react-router-dom"
import EventPage, { EventPageHeader } from "components/layout/EventPage"
import Spinner from "components/elements/Spinner"
import LinkSelector from "components/elements/LinkSelector"
import { MenuDivider, MenuItem } from "components/dialogs/Menu"
import { EVENT_PORTAL_HELP_ARTICLE_LINK } from "utilities/externalLinks"
import StyleAndConfiguration from "pages/EventShow/Forms/StyleAndConfiguration/StyleAndConfiguration"
import { useForms } from "queries/forms"
import { AccountUser, DioboxEvent, ModalName } from "sharedTypes"
import { eventPortalPath, eventPortalWalletPassBuilderPath, eventPortalWalletPassPath } from "utilities/routes"
import WalletPassBuilder from "components/WalletPass/WalletPassBuilder"
import useFeatureFlag from "services/featureFlags"
import usePermissions from "services/permissions"
import useModal from "services/useModal"

type Props = {
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
  accountUsers: AccountUser[]
}

const Portal = ({
  event: { id: eventId },
  event,
  updateEvent,
  accountUsers,
}: Props) => {
  const location = useLocation()
  const [eventWorkingCopy, setEventWorkingCopy] = useState(event)
  const { isLoading, data } = useForms(eventId)
  const showModal = useModal()
  const { walletPass: walletPassFlag } = useFeatureFlag()
  const { canViewSettings } = usePermissions()
  const walletPassEnabled = Boolean(walletPassFlag) && canViewSettings

  useEffect(() => {
    setEventWorkingCopy(event)
  }, [
    event.portalEventImageUrl, event.portalLogoUrl, event.translations, event.portalPrimaryColor,
    event.portalTopNavBackgroundColor, event.portalTopNavTextColor, event.portalBackgroundColor,
  ])

  if (isLoading) {
    return <Spinner />
  }

  const forms = data?.data ?? []

  const handleChangeEventWorkingCopy = (values: Partial<DioboxEvent>) => {
    setEventWorkingCopy((oldEventWorkingCopy) => ({ ...oldEventWorkingCopy, ...values }))
  }

  return (
    <EventPage
      scrollableBody={false}
      header={(
        <EventPageHeader
          icon="guest-portal"
          title="Portal"
          items={event.urls && (
            <LinkSelector
              label="Portal Link"
              url={event.urls.portal}
              actions={[
                <MenuItem key="event-link-settings" onClick={() => showModal(ModalName.EventLink)}>
                  Event Link Settings
                </MenuItem>,
                <MenuDivider key="about-divider" m={0} />,
                <MenuItem key="about" as="a" href={EVENT_PORTAL_HELP_ARTICLE_LINK} target="_blank" rel="noopener noreferrer">
                  Understanding the Event Portal
                </MenuItem>,
              ]}
            />
          )}
        />
      )}
      body={(
        <Flex h="full" justifyContent="center">
          <Switch location={location}>
            {walletPassEnabled && (
              <Route path={eventPortalWalletPassBuilderPath(eventId)}>
                <WalletPassBuilder
                  event={event}
                  updateEvent={updateEvent}
                  backPath={eventPortalWalletPassPath(eventId)}
                />
              </Route>
            )}
            <Route path={`${eventPortalPath(eventId)}/:subpath?`}>
              <StyleAndConfiguration
                variant="portal"
                pathFor={(subPath) => eventPortalPath(eventId, subPath)}
                event={event}
                updateEvent={updateEvent}
                onChangeEventWorkingCopy={handleChangeEventWorkingCopy}
                eventWorkingCopy={eventWorkingCopy}
                forms={forms}
                accountUsers={accountUsers}
                onResetEventWorkingCopy={() => setEventWorkingCopy(event)}
              />
            </Route>
          </Switch>
        </Flex>
      )}
    />
  )
}

export default Portal
